import { Controller } from "@hotwired/stimulus"
import DOMPurify from "dompurify"

// Connects to data-controller="infinite-scroll"
export default class extends Controller {
  static targets = ["list", "observer", "loader"]
  static values = {
    url: String,
    pagesCount: Number,
    cursor: Boolean
  }

  page = 1
  loading = false
  finished = false

  connect() {
    this.createObserver()
  }

  disconnect() {
    this.destroyObserver()
  }

  createObserver() {
    if (this.observer) return

    this.observer = new IntersectionObserver(
      entries => {
        const entry = entries[0]
        if (this.shouldLoad(entry)) {
          this.load()
        }
      },
      {
        root: this.scrollRoot(),
        rootMargin: "0px 0px 200px 0px"
      }
    )

    if (this.hasObserverTarget) {
      this.observer.observe(this.observerTarget)
    }
  }

  shouldLoad(entry) {
    return entry.isIntersecting && !this.loading && !this.finished && this.withinPageLimit()
  }

  withinPageLimit() {
    if (!this.hasPagesCountValue) return true

    return this.page < this.pagesCountValue
  }

  scrollRoot() {
    let element = this.element
    while (element) {
      const overflowY = getComputedStyle(element).overflowY
      if (overflowY === "auto" || overflowY === "scroll") return element
      element = element.parentElement
    }
    return null
  }

  destroyObserver() {
    if (this.observer) {
      this.observer.unobserve(this.observerTarget)
    }
    this.observer = null
  }

  async load() {
    this.loading = true
    this.showLoader()

    try {
      const response = await fetch(this.nextPageUrl(), { headers: { Accept: "text/html" } })
      if (response.ok) {
        const html = await response.text()
        if (html.trim() === "") {
          this.finished = true
        } else {
          this.page += 1
          this.listTarget.insertAdjacentHTML("beforeend", this.sanitizeHtml(html))
          this.refreshObserver()
        }
      }
    } catch (error) {
      console.error("Infinite scroll load error:", error)
    } finally {
      this.loading = false
      this.hideLoader()
    }
  }

  refreshObserver() {
    if (this.finished || !this.hasObserverTarget) return

    this.observer.unobserve(this.observerTarget)
    this.observer.observe(this.observerTarget)
  }

  showLoader() {
    if (this.hasLoaderTarget) this.loaderTarget.classList.remove("hidden")
  }

  hideLoader() {
    if (this.hasLoaderTarget) this.loaderTarget.classList.add("hidden")
  }

  nextPageUrl() {
    const url = new URL(this.urlValue, window.location.origin)
    if (this.cursorValue) {
      url.searchParams.set("before", this.lastRowId())
    } else {
      url.searchParams.set("page", this.page + 1)
    }
    return url.toString()
  }

  lastRowId() {
    const rows = this.listTarget.querySelectorAll("[data-row-id]")
    return rows.length ? rows[rows.length - 1].dataset.rowId : ""
  }

  sanitizeHtml(html) {
    return DOMPurify.sanitize(html)
  }
}
